import { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../config/api';
import { toast } from 'react-hot-toast';

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image', 'code-block'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet',
  'align',
  'link', 'image', 'code-block'
];

function CreatePost() {
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [categories, setCategories] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageCaption, setImageCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const editorRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState('');

  useEffect(() => {
    // Check if user exists and is not admin
    if (user && user.role !== 'admin') {
      console.log('User role:', user.role); // For debugging
      navigate('/');
    }
  }, [user, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        e.target.value = ''; // Clear the file input
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        e.target.value = '';
        return;
      }

      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError(''); // Clear any previous errors
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!content?.trim()) {
      setError('Content is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create FormData object
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('content', content.trim());
      
      if (categories.trim()) {
        formData.append('categories', categories.trim());
      }
      
      if (tags.trim()) {
        formData.append('tags', tags.trim());
      }
      
      if (image) {
        formData.append('image', image);
      }
      
      if (imageCaption?.trim()) {
        formData.append('imageCaption', imageCaption.trim());
      }

      const response = await apiClient.post('/posts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Check if response has data property first
      if (response.data) {
        // Navigate to the new post using the _id from the response
        navigate(`/post/${response.data._id}`);
        toast.success('Post created successfully!');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      setError(error.response?.data?.message || error.message || 'Failed to create post');
      toast.error('Failed to create post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is not logged in or not an admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <h1 className="text-2xl font-bold">
            Access Denied
          </h1>
          <p className="mt-2">
            Only administrators can create posts. Please contact an administrator if you need access.
          </p>
          <Link to="/" className="mt-4 inline-block text-blue-500 hover:underline">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Create New Post</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-1">
            Title
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 border rounded"
            required
          />
        </div>

        <div>
          <label htmlFor="categories" className="block text-sm font-medium mb-1">
            Categories (comma-separated)
          </label>
          <input
            id="categories"
            type="text"
            value={categories}
            onChange={(e) => setCategories(e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="technology, programming"
          />
        </div>

        <div>
          <label htmlFor="tags" className="block text-sm font-medium mb-1">
            Tags (comma-separated)
          </label>
          <input
            id="tags"
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="react, javascript"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Featured Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="w-full"
          />
          {imagePreview && (
            <div className="mt-2">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-w-md h-auto"
              />
              <input
                type="text"
                value={imageCaption}
                onChange={(e) => setImageCaption(e.target.value)}
                placeholder="Image caption (optional)"
                className="mt-2 w-full p-2 border rounded"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Content
          </label>
          <ReactQuill
            ref={editorRef}
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            theme="snow"
            className="bg-white dark:bg-gray-800 rounded-lg"
            style={{ height: '500px', marginBottom: '50px' }}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Publishing...' : 'Publish Post'}
        </button>
      </form>
    </div>
  );
}

export default CreatePost;

